import type { ProductPricing } from "@constructorfleet/offerings-sdk";

export const SITE_NAME = "ConstructorFleet";
export const SITE_URL = "https://constructorfleet.com";
export const DEFAULT_DESCRIPTION =
  "Automated check-ins, call-ins, and compliance reporting for daily obligations that carry real consequences when missed.";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/banner-teal.png`;

function formatDateForSitemap(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

type OfferingMarketing = {
  summary?: string;
  description?: string;
};

type OfferingRoutes = {
  productPath: string;
  signupPath?: string;
  demoPath?: string;
};

export type SeoOffering = {
  id: string;
  name: string;
  marketing?: OfferingMarketing;
  routes: OfferingRoutes;
  billing?: {
    pricing?: Pick<ProductPricing, "price" | "interval"> & Partial<ProductPricing>;
  };
};

export type PublicSeoPage = {
  path: string;
  title: string;
  description: string;
  changefreq: "weekly" | "monthly";
  priority: number;
};

export type SitemapLastmodState = Record<
  string,
  {
    signature: string;
    lastmod: string;
  }
>;

const CORE_PUBLIC_PAGES: PublicSeoPage[] = [
  {
    path: "/",
    title: "Automated Check-ins & Compliance Reporting",
    description:
      "We automate the calls, check-ins, and reporting deadlines that carry real consequences when they slip through the cracks.",
    changefreq: "weekly",
    priority: 1,
  },
  {
    path: "/about",
    title: "About",
    description:
      "ConstructorFleet is built by two engineers with 40+ years of combined experience automating the daily obligations that carry real consequences when missed.",
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/pricing",
    title: "Pricing",
    description:
      "Simple, straightforward pricing for automated check-ins and compliance reporting. No hidden fees, no long-term contracts.",
    changefreq: "weekly",
    priority: 0.8,
  },
  {
    path: "/privacy",
    title: "Privacy Policy",
    description:
      "Read how ConstructorFleet collects, uses, stores, and protects account and compliance workflow data.",
    changefreq: "monthly",
    priority: 0.4,
  },
  {
    path: "/terms",
    title: "Terms of Service",
    description:
      "Review the service terms, responsibilities, billing expectations, and account rules for ConstructorFleet.",
    changefreq: "monthly",
    priority: 0.4,
  },
  {
    path: "/refunds",
    title: "Refund Policy",
    description:
      "Understand when refunds are available, how cancellations work, and what to expect from ConstructorFleet billing.",
    changefreq: "monthly",
    priority: 0.4,
  },
];

const FOUNDERS = [
  {
    "@type": "Person",
    name: "Alan Janis",
    givenName: "Alan",
    familyName: "Janis",
    jobTitle: "Co-Founder",
  },
  {
    "@type": "Person",
    name: "Teagan Glenn",
    givenName: "Teagan",
    familyName: "Glenn",
    jobTitle: "Co-Founder",
  },
] as const;

function normalizePath(path: string) {
  if (!path) {
    return "/";
  }

  return path.startsWith("/") ? path : `/${path}`;
}

function dedupePages(pages: PublicSeoPage[]) {
  const seen = new Set<string>();
  return pages.filter((page) => {
    if (seen.has(page.path)) {
      return false;
    }

    seen.add(page.path);
    return true;
  });
}

function getOfferingDescription(offering: SeoOffering) {
  return offering.marketing?.description ?? offering.marketing?.summary ?? DEFAULT_DESCRIPTION;
}

function getOfferingPricing(offering: SeoOffering) {
  return offering.billing?.pricing;
}

export function formatUsdPrice(pricing?: Pick<ProductPricing, "price" | "interval">) {
  if (!pricing || typeof pricing.price !== "number") {
    return null;
  }

  const intervalSuffix =
    pricing.interval === "one-time" ? "one-time" : pricing.interval ? `/${pricing.interval}` : "";

  return `$${(pricing.price / 100).toFixed(2)}${intervalSuffix}`;
}

export function createPublicSeoPages(offerings: readonly SeoOffering[] = []) {
  const offeringPages = offerings.flatMap((offering) => {
    const description = getOfferingDescription(offering);
    const pages: PublicSeoPage[] = [
      {
        path: normalizePath(offering.routes.productPath),
        title: offering.name,
        description,
        changefreq: "weekly",
        priority: 0.8,
      },
    ];

    // Signup routes are intentionally excluded from public SEO surfaces
    // (sitemap, llms.txt, canonical resolution). They are auth-gated entry
    // points that client-redirect to /login, so the prerender step skips them
    // and S3 falls back to the SPA shell — listing them produced soft-404s /
    // homepage-duplicate canonicals. Keep them out of the crawlable surface.
    if (offering.routes.demoPath) {
      pages.push({
        path: normalizePath(offering.routes.demoPath),
        title: `${offering.name} Demo`,
        description: `See how ${offering.name} confirms check-ins, records results, and gives you a clear audit trail.`,
        changefreq: "monthly",
        priority: 0.6,
      });
    }

    return pages;
  });

  return dedupePages([...CORE_PUBLIC_PAGES, ...offeringPages]);
}

export function findPublicSeoPage(pathname: string, offerings: readonly SeoOffering[] = []) {
  return createPublicSeoPages(offerings).find((page) => page.path === pathname) ?? null;
}

function sitemapPageSignature(page: PublicSeoPage) {
  return JSON.stringify({
    path: page.path,
    title: page.title,
    description: page.description,
    changefreq: page.changefreq,
    priority: page.priority,
  });
}

export function createSitemapLastmodState(
  pages: readonly PublicSeoPage[],
  previousState: SitemapLastmodState = {},
  now = new Date(),
  existingLastmodByPath: Record<string, string> = {}
): SitemapLastmodState {
  const today = formatDateForSitemap(now);

  return Object.fromEntries(
    pages.map((page) => {
      const signature = sitemapPageSignature(page);
      const previous = previousState[page.path];
      const lastmod =
        previous?.signature === signature
          ? previous.lastmod
          : previous
            ? today
            : existingLastmodByPath[page.path] || today;
      return [page.path, { signature, lastmod }];
    })
  );
}

export function buildSitemapXml(
  pages: PublicSeoPage[],
  now = new Date(),
  lastmodByPath: Record<string, string> = {}
) {
  const lastModified = formatDateForSitemap(now);
  const urls = pages
    .map(
      (page) =>
        `  <url>\n    <loc>${SITE_URL}${page.path}</loc>\n    <lastmod>${lastmodByPath[page.path] || lastModified}</lastmod>\n    <changefreq>${page.changefreq}</changefreq>\n    <priority>${page.priority.toFixed(1)}</priority>\n  </url>`
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>`;
}

export function buildLlmsTxt(offerings: readonly SeoOffering[] = []) {
  const publicPages = createPublicSeoPages(offerings)
    .map((page) => `- [${page.title}](${SITE_URL}${page.path})`)
    .join("\n");
  const offeringSection = offerings
    .map((offering) => {
      const price = formatUsdPrice(getOfferingPricing(offering));
      const priceLine = price ? ` (${price})` : "";
      return `- [${offering.name}${priceLine}](${SITE_URL}${normalizePath(offering.routes.productPath)})\n  ${getOfferingDescription(offering)}`;
    })
    .join("\n");

  return [
    `# ${SITE_NAME}`,
    "",
    DEFAULT_DESCRIPTION,
    "",
    "License: Proprietary",
    "Creator: ConstructorFleet L.L.C.",
    "Primary site: [constructorfleet.com](https://constructorfleet.com)",
    "",
    "## Public Pages",
    publicPages,
    "",
    "## Offerings",
    offeringSection || "- No public offerings published.",
    "",
    "## Notes",
    "- ConstructorFleet automates daily check-ins, call-ins, and compliance reporting workflows.",
    "- Private dashboards, admin tools, and account pages are not public reference material.",
  ].join("\n");
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/ConstructorFleet Logo.png`,
    description: DEFAULT_DESCRIPTION,
    founders: [...FOUNDERS],
  };
}

export function buildWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    publisher: {
      "@id": `${SITE_URL}#organization`,
    },
  };
}

export function buildBreadcrumbJsonLd(page: Pick<PublicSeoPage, "path" | "title">) {
  const itemListElement = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  if (page.path !== "/") {
    itemListElement.push({
      "@type": "ListItem",
      position: 2,
      name: page.title,
      item: `${SITE_URL}${page.path}`,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}

export function buildOfferingStructuredData(offerings: readonly SeoOffering[]) {
  return offerings.map((offering) => {
    const pricing = getOfferingPricing(offering);

    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: offering.name,
      description: getOfferingDescription(offering),
      serviceType: "Compliance automation",
      provider: {
        "@id": `${SITE_URL}#organization`,
      },
      url: `${SITE_URL}${normalizePath(offering.routes.productPath)}`,
      offers: pricing
        ? {
            "@type": "Offer",
            url: `${SITE_URL}${normalizePath(offering.routes.productPath)}`,
            price: (pricing.price / 100).toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            category: "SaaS",
          }
        : undefined,
    };
  });
}

export function buildStructuredDataForPath(
  pathname: string,
  offerings: readonly SeoOffering[] = []
) {
  const page = findPublicSeoPage(pathname, offerings);

  if (!page) {
    return [];
  }

  const structuredData: Record<string, unknown>[] = [
    buildOrganizationJsonLd(),
    buildWebsiteJsonLd(),
  ];

  if (page.path !== "/") {
    structuredData.push(buildBreadcrumbJsonLd(page));
  }

  if (page.path === "/" || page.path === "/pricing") {
    structuredData.push(...buildOfferingStructuredData(offerings));
    return structuredData;
  }

  const matchingOffering = offerings.find((offering) => {
    const offeringPaths = [
      normalizePath(offering.routes.productPath),
      offering.routes.signupPath ? normalizePath(offering.routes.signupPath) : null,
      offering.routes.demoPath ? normalizePath(offering.routes.demoPath) : null,
    ].filter((value): value is string => Boolean(value));

    return offeringPaths.includes(page.path);
  });

  if (matchingOffering) {
    structuredData.push(...buildOfferingStructuredData([matchingOffering]));
  }

  return structuredData;
}
